import { AHSeparator, AHText, Box, ListItem } from "arkh";
import { useLazyLoadQuery, graphql } from "react-relay";
import { OrgUsersQuery } from "./__generated__/OrgUsersQuery.graphql";
import { FlatList } from "react-native";

import { useCallback, useMemo, useState } from "react";
import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";

import { OrgSearchInput } from "../../OrgSearchInput";
import { WorkspaceEmptyListItem } from "../../WorkspaceEmptyListItem";
import { listGutterStyle } from "../../guterStyle";
import { useOrgHomeDetailLayout } from "../../useOrgHomeDetailContainerStyle";
import { OrgAdminScreenProps } from "../OrgAdmin";
import { OrgButton } from "../../OrgButton";

function OrgUsersListHeader(props: {
  search: string;
  setSearch: (_: string) => void;
}) {
  const intl_tokens = useIntlTokens();
  return (
    <Box color="surface-background-light" spaceTop="medium">
      <OrgSearchInput
        placeholder={intl_tokens.Search}
        value={props.search}
        onChangeText={props.setSearch}
      />
      <Box color="surface-background-light">
        <ListItem displayMode="TABLE" disabled border="border-primary">
          <Box
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <AHText type="subtitle">{intl_tokens.Name}</AHText>
          </Box>
        </ListItem>
        <AHSeparator />
      </Box>
    </Box>
  );
}

function OrgUserListItem(props: {
  isLast: boolean;
  name: string;
  email: string;
  onPress: () => void;
}) {
  return (
    <ListItem
      isLast={props.isLast}
      onPress={props.onPress}
      right={<ListItem.NavArrow />}
      border="border-primary"
      displayMode="TABLE"
    >
      <Box>
        <Box
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <ListItem.Title>{props.name}</ListItem.Title>
          <Box spaceRight="medium" />
        </Box>
        <ListItem.SubTitle>{props.email}</ListItem.SubTitle>
      </Box>
    </ListItem>
  );
}

function InnerOrgUsersScreen(
  props: OrgAdminScreenProps<"OrgAdminUserAdmin"> & { fetchKey: string }
) {
  const [search, setSearch] = useState("");
  const data = useLazyLoadQuery<OrgUsersQuery>(
    graphql`
      query OrgUsersQuery {
        organisation @required(action: THROW) {
          id
          name
          users {
            edges {
              node {
                user {
                  id
                  name
                  email
                }
                orgRole
              }
            }
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const listData = useMemo(() => {
    const stores =
      data.organisation.users.edges
        .map((e) => e?.node?.user)
        .sort((a, b) =>
          a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase() ? 1 : -1
        ) ?? [];
    if (search.length > 0) {
      const normalizedSearch = search.toLocaleLowerCase();
      return stores.filter(
        (s) =>
          s.name.toLocaleLowerCase().includes(normalizedSearch) ||
          s.email.toLocaleLowerCase().includes(normalizedSearch)
      );
    }
    return stores;
  }, [data.organisation.users, search]);

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      return (
        <OrgUserListItem
          email={item.email}
          name={item.name}
          isLast={index === listData.length - 1}
          onPress={() => {
            props.navigation.navigateDeprecated("OrgUserEdit", {
              screen: "OrgUserEditScreen",
              params: {
                id: item.id,
                name: item.name,
                email: item.email,
              },
            });
          }}
        />
      );
    },
    [listData.length, props.navigation]
  );
  const { navigateDeprecated } = props.navigation;

  const detailLayout = useOrgHomeDetailLayout();
  const intl_tokens = useIntlTokens();
  return (
    <>
      <FlatList
        style={[
          {
            flex: 1,
            marginBottom: detailLayout.keyboardBottomInset,
          },
          listGutterStyle,
        ]}
        contentContainerStyle={[
          detailLayout.contentContainerStyle,
          { minWidth: 300 },
        ]}
        data={listData}
        keyboardShouldPersistTaps="handled"
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
        ItemSeparatorComponent={AHSeparator}
        ListEmptyComponent={WorkspaceEmptyListItem}
        stickyHeaderIndices={[0]}
        ListHeaderComponent={
          <OrgUsersListHeader search={search} setSearch={setSearch} />
        }
      />
      <AHSeparator />
      <Box
        color="surface"
        space="medium"
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <OrgButton
          title={intl_tokens.InviteUser}
          onPress={() => navigateDeprecated("OrgUserInvite", {})}
          backgroundColor="primary"
          tintColor="text-inverted"
          icon="add"
        />
      </Box>
    </>
  );
}

export function OrgUsersScreen(
  props: OrgAdminScreenProps<"OrgAdminUserAdmin">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey: string) => <InnerOrgUsersScreen {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}
